import { useEffect, useLayoutEffect, useSyncExternalStore } from 'react';

/** The theme actually painted: `[data-theme]` on <html>. */
export type Theme = 'light' | 'dark';
/** What the owner picked. `auto` follows the device and is the default. */
export type ThemePreference = Theme | 'auto';

/** Cycle order of the account-menu button: one click, the next mode. */
export const THEME_PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'auto'];

/** Shared with the portal's boot script in index.html, which paints the
 *  theme before the bundle loads. Keep the two readers in step.
 *  Not the old `tc-theme`: that one was written with the RESOLVED theme on
 *  every page load, so it can't tell a real choice from the device's at the
 *  time, and reading it would pin existing owners off the `auto` default. */
export const THEME_STORAGE_KEY = 'tc-theme-mode';

const listeners = new Set<() => void>();
let preference: ThemePreference | undefined;
/** Mounted public pages: while any is on screen the device decides. */
let systemLocks = 0;
let watching = false;
/** Held, not re-created: the `change` listener lives on this one object. */
let darkQuery: MediaQueryList | undefined;

function isPreference(v: unknown): v is ThemePreference {
  return v === 'light' || v === 'dark' || v === 'auto';
}

function media(): MediaQueryList | null {
  if (!darkQuery && typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    darkQuery = window.matchMedia('(prefers-color-scheme: dark)');
  }
  return darkQuery ?? null;
}

function systemTheme(): Theme {
  return media()?.matches ? 'dark' : 'light';
}

function readPreference(): ThemePreference {
  if (preference === undefined) {
    let stored: string | null = null;
    try {
      stored = typeof localStorage !== 'undefined' ? localStorage.getItem(THEME_STORAGE_KEY) : null;
    } catch {
      /* storage may be unavailable (private mode) */
    }
    // Nothing stored (a new owner) or something unknown → auto.
    preference = isPreference(stored) ? stored : 'auto';
  }
  return preference;
}

function effectiveTheme(): Theme {
  const p = systemLocks > 0 ? 'auto' : readPreference();
  return p === 'auto' ? systemTheme() : p;
}

/** Paint `[data-theme]` and, when the page provides both colours as
 *  `data-light`/`data-dark`, the browser-chrome `theme-color` meta. */
function apply(): void {
  if (typeof document === 'undefined') return;
  const theme = effectiveTheme();
  document.documentElement.setAttribute('data-theme', theme);
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  const color = meta?.dataset[theme];
  if (meta && color) meta.content = color;
}

function emit(): void {
  apply();
  listeners.forEach((l) => l());
}

/** Installed once, on first subscribe — never at import, so importing the
 *  barrel can't repaint a page the boot script already got right. */
function watch(): void {
  if (watching || typeof window === 'undefined') return;
  watching = true;
  // The device switched light/dark: only `auto` (and public pages) follow.
  media()?.addEventListener('change', emit);
  // Another tab changed the preference: follow it here too.
  window.addEventListener?.('storage', (e) => {
    if (e.key !== THEME_STORAGE_KEY) return;
    preference = isPreference(e.newValue) ? e.newValue : 'auto';
    emit();
  });
}

function subscribe(listener: () => void): () => void {
  watch();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Save the owner's choice and repaint (unless a public page holds the
 *  device theme, in which case it applies once that page is gone). */
export function setThemePreference(next: ThemePreference): void {
  preference = next;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    /* storage may be unavailable (private mode) */
  }
  emit();
}

/** The mode after `current` in {@link THEME_PREFERENCES}. */
export function nextThemePreference(current: ThemePreference): ThemePreference {
  return THEME_PREFERENCES[(THEME_PREFERENCES.indexOf(current) + 1) % THEME_PREFERENCES.length] ?? 'auto';
}

/** The owner's saved preference plus the theme currently painted. */
export function useThemePreference(): {
  preference: ThemePreference;
  theme: Theme;
  setPreference: (next: ThemePreference) => void;
} {
  const p = useSyncExternalStore(subscribe, readPreference, () => 'auto' as const);
  const theme = useSyncExternalStore(subscribe, effectiveTheme, () => 'light' as const);
  return { preference: p, theme, setPreference: setThemePreference };
}

// Layout effect in the browser so a page switch repaints before it is seen;
// plain effect on the server, where layout effects warn.
const useIsomorphicLayoutEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * For pages nobody configures (sign-in, landing): while mounted, paint the
 * device's light/dark and ignore the saved preference. The preference comes
 * back as soon as the page unmounts.
 */
export function useSystemTheme(): void {
  useIsomorphicLayoutEffect(() => {
    watch();
    systemLocks += 1;
    emit();
    return () => {
      systemLocks -= 1;
      emit();
    };
  }, []);
}
